import { css, html, LitElement } from 'lit';
import { Image } from '../Image/Image';

class AttachmentBox extends LitElement {
  static properties = {
    bgColor: { type: String },
    textColor: { type: String },
    documentIcon: { type: String },
    mediaIcon: { type: String },
    documentIconHeight: { type: String },
    documentIconWidth: { type: String },
    mediaIconHeight: { type: String },
    mediaIconWidth: { type: String },
    containerBorderWidth: { type: String },
    containerBorderColor: { type: String },
    containerBorderStyle: { type: String },
    width: { type: String },
    flexDirection: { type: String },
    padding: { type: String },
    iconGap: { type: String },
    containerBorderRadius: { type: String },
    onFileUploaded: { type: String },
    maxFileSize: { type: Number }, // in MB
    onCameraClick: { type: Function }, // in MB
  };

  constructor() {
    super();
    this.maxFileSize = 50; // Default max file size in MB
    this.allowedImageTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/heic',
      'image/heif'
    ];
    this.allowedVideoTypes = [
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-msvideo',
      'video/x-matroska'
    ];
  }

  static styles = css`
    .attachment-box-container {
      display: flex;
      gap: 2px;
      color: white;
      align-items: flex-start;
    }

    .document-container,
    .media-container {
      cursor: pointer !important;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    input[type='file'] {
      opacity: 0;
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      cursor: pointer;
    }

    span {
      font-size: 16px;
      font-weight: 400;
      color: var(--text-primary-color);
    }

    .attachment-box-container div {
      display: flex;
      align-items: center;
      padding: 8px;
      gap: 5px;
      cursor: pointer;
      justify-content: flex-start;
      width: 100%;
    }
  `;

  validateFile(file) {
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > this.maxFileSize) {
      this.dispatchEvent(
        new CustomEvent('file-error', {
          detail: {
            message: `File ${file.name} exceeds maximum size of ${this.maxFileSize}MB`,
            file: file
          },
          bubbles: true,
          composed: true,
        })
      );
      return false;
    }

    // Check file type

    return true;
  }

  handleFileChange(event, fileType) {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      if (this.validateFile(file)) {
        validFiles.push({
          file,
          type: this.allowedImageTypes.includes(file.type) ? 'image' : 'video'
        });
      } else {
        invalidFiles.push(file);
      }
    });
    if (validFiles.length > 0) {
      this.dispatchEvent(
        new CustomEvent('file-uploaded', {
          detail: {
            fileType,
            files: validFiles.map(vf => vf.file),
            fileTypes: validFiles.map(vf => vf.type),
            totalFiles: validFiles.length
          },
          bubbles: true,
          composed: true,
        })
      );
    }

    // Clear the input to allow selecting the same file again
    event.target.value = '';
  }

  render() {
    return html`
      <div
        class="attachment-box-container"
        style="background-color: ${this.bgColor}; 
        border-width: ${this.containerBorderWidth};
        border-color: ${this.containerBorderColor};
        border-style: ${this.containerBorderStyle};
        border-radius: ${this.containerBorderRadius || '10px'};
        width: ${this.width};
        flex-direction: ${this.flexDirection};
        padding: ${this.padding};
        gap: ${this.iconGap};
      "
      >
        <div class="document-container">
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#7f66ff">
            <path
              d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-520h200L520-800v200Z"
            />
          </svg>
          <input id="documentUpload" type="file" accept=".doc,.docx,.pdf,.rtf,.txt,.odt" @change="${e => this.handleFileChange(e, 'Document')}" />
          <span>Documents</span>
        </div>

        <div class="media-container">
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#007bfc">
            <path
              d="M120-120q-33 0-56.5-23.5T40-200v-520h80v520h680v80H120Zm160-160q-33 0-56.5-23.5T200-360v-440q0-33 23.5-56.5T280-880h200l80 80h280q33 0 56.5 23.5T920-720v360q0 33-23.5 56.5T840-280H280Zm80-160h400L622-620l-92 120-62-80-108 140Z"
            />
          </svg>
          <input
            id="mediaUpload"
            type="file"
            multiple
            accept="${this.allowedImageTypes.join(',')},${this.allowedVideoTypes.join(',')}" 
            @change="${e => this.handleFileChange(e, 'Media')}" 
          />
          <span>Photos & Videos</span>
        </div>

      <div class="camera-container" @click="${this.onCameraClick}">
          <svg xmlns="http://www.w3.org/2000/svg" height="20px" viewBox="0 -960 960 960" width="20px" fill="#ff2e74">
            <path
              d="M480-260q75 0 127.5-52.5T660-440q0-75-52.5-127.5T480-620q-75 0-127.5 52.5T300-440q0 75 52.5 127.5T480-260Zm0-80q-42 0-71-29t-29-71q0-42 29-71t71-29q42 0 71 29t29 71q0 42-29 71t-71 29ZM160-120q-33 0-56.5-23.5T80-200v-480q0-33 23.5-56.5T160-760h126l74-80h240l74 80h126q33 0 56.5 23.5T880-680v480q0 33-23.5 56.5T800-120H160Z"
            />
          </svg>
          <span>Camera</span>
        </div>
      </div>
    `;
  }
}

customElements.define('attachment-box', AttachmentBox);